import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Plus, X } from 'shared/icons';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import ConfirmModal from '../ui/ConfirmModal';
import ErrorCard from '../ui/ErrorCard';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import { MONO_CLASS } from '../ui/Mono';
import Select from '../ui/Select';
import { SkeletonTable } from '../ui/Skeleton';
import StandardTable from '../ui/StandardTable';
import { useToast } from '../ui/Toast';
import { ExtractListRows } from './payload';
import { useWarehouseLabels } from './warehouseLabels';

// Limite alto nas listagens de cadastro: são registries pequenos (dezenas de
// linhas); a paginação visual fica por conta do StandardTable (client-side).
const LIST_PARAMS = { limit: 500 };

function buildEmptyForm(fields) {
    const form = {};
    for (const field of fields) {
        if (field.type === 'checkbox') {
            form[field.name] = Boolean(field.defaultValue);
        } else if (field.type === 'multicheck') {
            // Conjunto de valores (ex.: papéis do operador). Array sempre.
            form[field.name] = Array.isArray(field.defaultValue) ? [...field.defaultValue] : [];
        } else if (field.type === 'select') {
            form[field.name] = field.defaultValue ?? field.options?.[0]?.value ?? '';
        } else {
            form[field.name] = field.defaultValue ?? '';
        }
    }
    return form;
}

function buildFormFromRow(fields, row) {
    const form = {};
    for (const field of fields) {
        if (field.type === 'checkbox') {
            form[field.name] = Boolean(row?.[field.name]);
        } else if (field.type === 'date') {
            // O wire devolve datetime ISO; o <input type="date"> exige AAAA-MM-DD.
            form[field.name] = String(row?.[field.name] ?? '').slice(0, 10);
        } else if (field.type === 'multicheck') {
            form[field.name] = Array.isArray(row?.[field.name]) ? [...row[field.name]] : [];
        } else {
            form[field.name] = row?.[field.name] ?? '';
        }
    }
    return form;
}

// Campo condicional (ex.: ID do depósito no Bling só com a replicação ligada;
// funcionário do operador só na criação). Sem visibleWhen o campo é sempre
// visível. `context.isEditing` distingue criar de editar.
function isFieldVisible(field, form, context) {
    return typeof field.visibleWhen === 'function' ? Boolean(field.visibleWhen(form, context)) : true;
}

function buildWirePayload(fields, form, context) {
    const payload = {};
    for (const field of fields) {
        // Campo OCULTO fica fora do payload: enviar o valor de um campo que o
        // usuário nem viu apagaria dado em silêncio (ex.: desligar a replicação
        // não pode limpar o ID do Bling que estava guardado).
        if (!isFieldVisible(field, form, context)) {
            continue;
        }
        if (field.type === 'checkbox') {
            payload[field.name] = Boolean(form[field.name]);
        } else if (field.type === 'date') {
            // '' vira null (limpa a data); o backend valida AAAA-MM-DD.
            payload[field.name] = String(form[field.name] ?? '').trim() || null;
        } else if (field.type === 'multicheck') {
            payload[field.name] = Array.isArray(form[field.name]) ? form[field.name] : [];
        } else if (field.type === 'number') {
            // O wire espera NÚMERO (ex.: porta da impressora); '' vira null
            // (campo vazio = usar o default do backend). Faixa valida no back.
            const text = String(form[field.name] ?? '').trim();
            payload[field.name] = text === '' ? null : Number(text);
        } else {
            payload[field.name] = String(form[field.name] ?? '').trim();
        }
    }
    return payload;
}

/**
 * Seção genérica de cadastro do módulo Armazém (console do Supervisor):
 * listagem (StandardTable) + criar/editar em Modal + ativar/desativar via
 * PATCH `{active}`. SEM DELETE em lugar nenhum — política de soft delete.
 *
 * `config` (montar com useMemo no chamador — a identidade dispara o reload):
 * - entityLabel / entityLabelPlural / countLabel / description
 * - list() / create(payload) / update(id, patch)  → métodos da api
 * - listKeys: chaves preferidas do envelope de listagem (ex.: ['operators'])
 * - columns: colunas do StandardTable (Status e Ações são acrescentadas aqui)
 * - fields: campos do formulário do modal
 *   { name, label, type: 'text'|'select'|'checkbox'|'date'|'number', options,
 *     required, mono, uppercase, placeholder, help, maxLength, min, max,
 *     visibleWhen(form, { isEditing }) }
 * - conflictField: campo apontado no erro 409 (default 'code')
 * - exportFileName
 * - headerExtra: nó opcional renderizado ao lado do botão "Novo" (ex.: o
 *   "Detectar na rede" da aba Impressoras)
 *
 * Props além do config:
 * - createPrefill: objeto opcional {campo: valor} — quando muda para não-nulo,
 *   abre o modal de CRIAÇÃO pré-preenchido (ex.: "+ cadastrar" da descoberta
 *   de impressoras) e avisa via onCreatePrefillConsumed.
 */
const RegistryManager = ({ config, createPrefill = null, onCreatePrefillConsumed }) => {
    const toast = useToast();
    const { label } = useWarehouseLabels();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [isModalOpen, setModalOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [form, setForm] = useState({});
    const [formError, setFormError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [rowToToggle, setRowToToggle] = useState(null);
    const [toggleLoading, setToggleLoading] = useState(false);

    const { list, listKeys } = config;

    // Sem `label` nas deps de propósito: a mensagem amigável é composta no
    // render — a chegada do dicionário remoto de rótulos não deve refazer o GET.
    const loadRows = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const data = await list(LIST_PARAMS);
            setRows(ExtractListRows(data, listKeys));
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [list, listKeys]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const openCreate = () => {
        setEditingRow(null);
        setForm(buildEmptyForm(config.fields));
        setFormError(null);
        setModalOpen(true);
    };

    // Criação pré-preenchida disparada de fora (ex.: descoberta de impressoras
    // → "+ cadastrar"): abre o modal de criação já com os valores e consome o
    // prefill (o pai zera; se algo mudar enquanto nulo, o efeito não faz nada).
    useEffect(() => {
        if (!createPrefill) {
            return;
        }
        setEditingRow(null);
        setForm({ ...buildEmptyForm(config.fields), ...createPrefill });
        setFormError(null);
        setModalOpen(true);
        onCreatePrefillConsumed?.();
    }, [createPrefill, config.fields, onCreatePrefillConsumed]);

    const openEdit = useCallback(
        (row) => {
            setEditingRow(row);
            setForm(buildFormFromRow(config.fields, row));
            setFormError(null);
            setModalOpen(true);
        },
        [config.fields],
    );

    const closeModal = () => {
        if (!saving) {
            setModalOpen(false);
        }
    };

    const setFieldValue = (field, rawValue) => {
        const value = field.uppercase && typeof rawValue === 'string' ? rawValue.toUpperCase() : rawValue;
        setForm((previous) => ({ ...previous, [field.name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const formContext = { isEditing: Boolean(editingRow) };
        for (const field of config.fields) {
            if (!isFieldVisible(field, form, formContext)) {
                continue;
            }
            if (field.required && field.type !== 'checkbox' && !String(form[field.name] ?? '').trim()) {
                setFormError({ field: field.name, message: label('wms.common.required') });
                return;
            }
        }

        const payload = buildWirePayload(config.fields, form, formContext);
        setSaving(true);
        setFormError(null);
        try {
            if (editingRow) {
                await config.update(editingRow.id, payload);
                toast.success(`${config.entityLabel} atualizado(a)`);
            } else {
                await config.create(payload);
                toast.success(`${config.entityLabel} criado(a)`);
            }
            setModalOpen(false);
            await loadRows();
        } catch (error) {
            if (error?.status === 409) {
                // 409 de código duplicado precisa aparecer legível, no campo certo.
                const serverMessage =
                    error.message && error.message !== 'Unknown error' ? error.message : label('wms.common.conflict');
                setFormError({ field: config.conflictField || 'code', message: serverMessage });
            } else if (error?.code === 'NETWORK_ERROR') {
                setFormError({ message: label('wms.collector.error_network') });
            } else {
                setFormError({ message: error?.message || 'Erro ao salvar' });
            }
        } finally {
            setSaving(false);
        }
    };

    const confirmToggle = async () => {
        if (!rowToToggle) {
            return;
        }
        const nextActive = !rowToToggle.active;
        setToggleLoading(true);
        try {
            await config.update(rowToToggle.id, { active: nextActive });
            toast.success(`${config.entityLabel} ${nextActive ? 'ativado(a)' : 'desativado(a)'}`);
            await loadRows();
        } catch (error) {
            const message =
                error?.code === 'NETWORK_ERROR' ? label('wms.collector.error_network') : error?.message || 'Erro ao salvar';
            toast.error(message);
        } finally {
            setToggleLoading(false);
            setRowToToggle(null);
        }
    };

    const columns = useMemo(
        () => [
            ...config.columns,
            {
                key: 'active',
                label: label('wms.common.status'),
                getValue: (row) => (row.active ? 1 : 0),
                render: (row) =>
                    row.active ? (
                        <Badge variant="success">{label('wms.common.active')}</Badge>
                    ) : (
                        <Badge variant="neutral">{label('wms.common.inactive')}</Badge>
                    ),
            },
            {
                key: 'actions',
                label: label('wms.common.actions'),
                sortable: false,
                searchable: false,
                exportable: false,
                getValue: () => '',
                render: (row) => (
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={(event) => {
                                event.stopPropagation();
                                openEdit(row);
                            }}
                        >
                            {label('wms.common.edit')}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={(event) => {
                                event.stopPropagation();
                                setRowToToggle(row);
                            }}
                            className={
                                row.active
                                    ? 'text-orange-600 hover:text-orange-700'
                                    : 'text-green-600 hover:text-green-700'
                            }
                            title={row.active ? label('wms.common.deactivate') : label('wms.common.activate')}
                        >
                            {row.active ? (
                                <>
                                    <X size={14} className="mr-1" />
                                    {label('wms.common.deactivate')}
                                </>
                            ) : (
                                <>
                                    <Check size={14} className="mr-1" />
                                    {label('wms.common.activate')}
                                </>
                            )}
                        </Button>
                    </div>
                ),
            },
        ],
        [config, label, openEdit],
    );

    return (
        <section className="space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="space-y-1">
                    <h2 className="text-lg font-semibold text-black dark:text-white">{config.entityLabelPlural}</h2>
                    {config.description ? (
                        <p className="max-w-3xl text-sm text-mono-600 dark:text-mono-400">{config.description}</p>
                    ) : null}
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                    {config.headerExtra}
                    <Button onClick={openCreate} className="w-full sm:w-auto">
                        <Plus size={18} className="mr-2" />
                        {`${label('wms.common.new')} — ${config.entityLabel}`}
                    </Button>
                </div>
            </div>

            {loading ? (
                <SkeletonTable columns={config.columns.length + 2} rows={6} />
            ) : loadError ? (
                <ErrorCard
                    title={config.entityLabelPlural}
                    message={`${label('wms.common.load_error')}\n${loadError}`}
                    action={label('wms.common.retry')}
                    actionFn={loadRows}
                />
            ) : (
                <StandardTable
                    rows={rows}
                    columns={columns}
                    rowKey={(row, index) => row.id || index}
                    countLabel={config.countLabel}
                    exportFileName={config.exportFileName}
                    emptyMessage="Nenhum registro encontrado."
                />
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={closeModal}
                title={
                    editingRow
                        ? `${label('wms.common.edit')} — ${config.entityLabel}`
                        : `${label('wms.common.new')} — ${config.entityLabel}`
                }
                size="md"
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    {config.fields.map((field) => {
                        if (!isFieldVisible(field, form, { isEditing: Boolean(editingRow) })) {
                            return null;
                        }
                        const fieldError = formError?.field === field.name ? formError.message : undefined;
                        if (field.type === 'select') {
                            return (
                                <Select
                                    key={field.name}
                                    label={field.label}
                                    value={form[field.name] ?? ''}
                                    onChange={(event) => setFieldValue(field, event.target.value)}
                                    errorMessage={fieldError}
                                    disabled={saving}
                                >
                                    {field.options.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Select>
                            );
                        }
                        if (field.type === 'multicheck') {
                            const selected = Array.isArray(form[field.name]) ? form[field.name] : [];
                            // `masterOption` (ex.: supervisor): quando marcado, TRAVA todas as
                            // outras opções marcadas — é a leitura visual de "faz tudo". O
                            // valor gravado passa a incluir todas; o backend deriva o resto.
                            const master = field.masterOption ?? null;
                            const masterActive = master ? selected.includes(master) : false;
                            const allValues = field.options.map((option) => option.value);
                            const toggle = (value) => {
                                let next;
                                if (master && value === master) {
                                    // Marcar o master seleciona TUDO; desmarcar solta as travas
                                    // (as outras seguem marcadas, agora editáveis).
                                    next = selected.includes(master)
                                        ? selected.filter((v) => v !== master)
                                        : allValues;
                                } else if (masterActive) {
                                    return; // travado pelo master — não faz nada
                                } else {
                                    next = selected.includes(value)
                                        ? selected.filter((v) => v !== value)
                                        : [...selected, value];
                                }
                                setFieldValue(field, next);
                            };
                            return (
                                <div key={field.name}>
                                    <span className="block text-sm font-medium text-black dark:text-white">
                                        {field.label}
                                    </span>
                                    {field.help ? (
                                        <span className="mb-2 block text-xs text-mono-500 dark:text-mono-400">
                                            {field.help}
                                        </span>
                                    ) : null}
                                    <div className="grid grid-cols-2 gap-2">
                                        {field.options.map((option) => {
                                            const isMaster = master && option.value === master;
                                            // Master marcado ⇒ as demais aparecem marcadas e travadas.
                                            const checked = masterActive ? true : selected.includes(option.value);
                                            const locked = masterActive && !isMaster;
                                            return (
                                                <label
                                                    key={option.value}
                                                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 dark:border-mono-800 ${
                                                        locked
                                                            ? 'border-mono-200 bg-mono-50 dark:bg-mono-900'
                                                            : 'border-mono-200'
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggle(option.value)}
                                                        disabled={saving || locked}
                                                        className="h-4 w-4"
                                                    />
                                                    <span className="text-sm">{option.label}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                    {masterActive && field.masterHint ? (
                                        <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">{field.masterHint}</p>
                                    ) : null}
                                    {fieldError ? (
                                        <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{fieldError}</p>
                                    ) : null}
                                </div>
                            );
                        }
                        if (field.type === 'checkbox') {
                            return (
                                <label key={field.name} className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(form[field.name])}
                                        onChange={(event) => setFieldValue(field, event.target.checked)}
                                        disabled={saving}
                                        className="mt-1 h-4 w-4"
                                    />
                                    <span>
                                        <span className="block text-sm font-medium text-black dark:text-white">
                                            {field.label}
                                        </span>
                                        {field.help ? (
                                            <span className="block text-xs text-mono-500 dark:text-mono-400">
                                                {field.help}
                                            </span>
                                        ) : null}
                                    </span>
                                </label>
                            );
                        }
                        return (
                            <div key={field.name}>
                                <Input
                                    label={field.label}
                                    type={field.type === 'date' || field.type === 'number' ? field.type : 'text'}
                                    value={form[field.name] ?? ''}
                                    onChange={(event) => setFieldValue(field, event.target.value)}
                                    placeholder={field.placeholder}
                                    errorMessage={fieldError}
                                    disabled={saving}
                                    maxLength={field.maxLength}
                                    min={field.min}
                                    max={field.max}
                                    inputMode={field.inputMode}
                                    autoComplete="off"
                                    className={field.mono ? MONO_CLASS : ''}
                                />
                                {field.help && !fieldError ? (
                                    <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">{field.help}</p>
                                ) : null}
                            </div>
                        );
                    })}
                    {formError && !formError.field ? (
                        <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                            {formError.message}
                        </p>
                    ) : null}

                    <div className="flex flex-col gap-3 pt-4 sm:flex-row">
                        <Button type="submit" loading={saving} className="flex-1">
                            {label('wms.common.save')}
                        </Button>
                        <Button type="button" onClick={closeModal} variant="secondary" className="sm:w-auto">
                            {label('wms.common.cancel')}
                        </Button>
                    </div>
                </form>
            </Modal>

            <ConfirmModal
                isOpen={Boolean(rowToToggle)}
                onClose={() => setRowToToggle(null)}
                onConfirm={confirmToggle}
                title={rowToToggle?.active ? label('wms.common.deactivate') : label('wms.common.activate')}
                message={
                    rowToToggle
                        ? `Confirma ${rowToToggle.active ? 'desativar' : 'ativar'} "${
                              rowToToggle.name || rowToToggle.label || rowToToggle.code || rowToToggle.id
                          }"?`
                        : ''
                }
                confirmText={rowToToggle?.active ? label('wms.common.deactivate') : label('wms.common.activate')}
                loading={toggleLoading}
            />
        </section>
    );
};

export default RegistryManager;
